import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import MobileLayout from "../components/layout/MobileLayout";
import { ArrowLeft, History, ChevronRight, Wallet, Plane, Zap } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";

/* =========================================================================
   AVIATOR — Deep space × Ember gold. Crash-style luxury with animated plane.
   ========================================================================= */

const PRESETS = [50, 100, 500, 1000, 5000];

function multColor(m) {
  if (m < 1.5) return "#f87171";       // rose
  if (m < 2)   return "#fb923c";       // orange
  if (m < 5)   return "#facc15";       // gold
  if (m < 10)  return "#a3e635";       // lime
  return "#22d3ee";                    // cyan (rare)
}

export default function AviatorGamePage() {
  const { user, refreshUser } = useAuth();
  const [round, setRound] = useState(null);
  const [amount, setAmount] = useState(50);
  const [autoCashout, setAutoCashout] = useState(""); // string; empty = manual
  const [placing, setPlacing] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [liveMult, setLiveMult] = useState(1.00);
  const [localElapsed, setLocalElapsed] = useState(0);
  const [winPopup, setWinPopup] = useState(null); // {amount, mult} on successful cashout
  const previousRoundIdRef = useRef(null);
  const rafRef = useRef(null);
  const flightStartLocalRef = useRef(null);
  const audioRef = useRef(null);

  const fetchRound = useCallback(async () => {
    try {
      const { data } = await api.get(`/aviator/current`);
      setRound(data);
      if (previousRoundIdRef.current && previousRoundIdRef.current !== data.round_id) {
        refreshUser();
      }
      previousRoundIdRef.current = data.round_id;
      // Anchor a local flight-start reference for smooth in-between-poll growth
      if (data.phase === "flying" && data.flight_elapsed != null) {
        flightStartLocalRef.current = Date.now() - data.flight_elapsed * 1000;
      } else if (data.phase !== "flying") {
        flightStartLocalRef.current = null;
      }
      if (data.phase === "flying") {
        setLiveMult(data.current_multiplier);
      } else if (data.phase === "crashed") {
        setLiveMult(data.crash_multiplier);
      } else {
        setLiveMult(1.00);
      }
    } catch (e) { console.error(e); }
  }, [refreshUser]);

  useEffect(() => {
    fetchRound();
    const id = setInterval(fetchRound, 1000);
    return () => clearInterval(id);
  }, [fetchRound]);

  // Smooth per-frame multiplier growth during flight (between polls)
  useEffect(() => {
    if (!round || round.phase !== "flying") {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    const RATE = 0.07;
    const tick = () => {
      if (flightStartLocalRef.current == null) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = (Date.now() - flightStartLocalRef.current) / 1000;
      const m = Math.exp(RATE * Math.max(0, elapsed));
      setLiveMult(Number(m.toFixed(2)));
      setLocalElapsed(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [round?.phase, round?.round_id]);

  const placeBet = async () => {
    if (round?.phase !== "betting") return toast.error("Betting is closed");
    if (round?.my_bet) return toast.error("Bet already placed");
    if (amount < (round?.min_bet || 50)) return toast.error(`Min bet ₹${round?.min_bet || 50}`);
    if ((user?.wallet_balance ?? 0) < amount) return toast.error("Insufficient balance");
    setPlacing(true);
    try {
      const body = { amount };
      const auto = parseFloat(autoCashout);
      if (auto && auto > 1) body.auto_cashout = auto;
      await api.post(`/aviator/bet`, body);
      toast.success(`₹${amount} bet locked ${auto ? `· auto @${auto}x` : ""}`);
      refreshUser();
      fetchRound();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPlacing(false); }
  };

  const cashOut = async () => {
    if (round?.phase !== "flying") return;
    if (!round?.my_bet || round?.my_bet?.cashout_multiplier != null) return;
    setCashingOut(true);
    try {
      const { data } = await api.post(`/aviator/cashout`);
      const won = round.my_bet.amount * data.cashout_multiplier;
      toast.success(`Cashed out at ${data.cashout_multiplier}x — ₹${won.toFixed(2)} won!`);
      setWinPopup({ amount: won.toFixed(2), mult: data.cashout_multiplier });
      setTimeout(() => setWinPopup(null), 2800);
      refreshUser();
      fetchRound();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCashingOut(false); }
  };

  // Mock "live players" feed for social proof — regenerates each round.
  // Simulates other players betting + cashouts.
  const livePlayers = useMemo(() => {
    if (!round) return [];
    // Deterministic per-round seed for stable feed
    let seed = 0;
    for (let i = 0; i < round.round_id.length; i++) seed = (seed * 31 + round.round_id.charCodeAt(i)) % 100000;
    const rng = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    const names = ["R***25", "A***u9", "M***r7", "S***a3", "K***1", "P***h", "V***y8", "N***j", "G***2", "H***x5", "T***4", "B***7"];
    const out = [];
    for (let i = 0; i < 10; i++) {
      const amt = [50, 100, 200, 500, 1000][Math.floor(rng() * 5)];
      // Some auto cashout at various targets — deterministic
      const rr = rng();
      let cashoutMult = null;
      if (rr < 0.55) cashoutMult = +(1.1 + rng() * 3).toFixed(2); // set target 1.1-4.1x
      out.push({
        name: names[i % names.length],
        amount: amt,
        target: cashoutMult, // will show as "cashed" if live mult ≥ target
      });
    }
    return out;
  }, [round?.round_id]);

  if (!round) return (
    <MobileLayout>
      <div className="h-64 flex items-center justify-center text-amber-200/70 tracking-widest text-xs">LOADING FLIGHT…</div>
    </MobileLayout>
  );

  const isBetting = round.phase === "betting";
  const isFlying  = round.phase === "flying";
  const isCrashed = round.phase === "crashed";
  const displayMult = isCrashed ? round.crash_multiplier : liveMult;
  const myBet = round.my_bet;
  const myCashed = myBet?.cashout_multiplier != null;
  const canCashOut = isFlying && myBet && !myCashed;

  // Plane path — driven by FLIGHT TIME (not multiplier) so plane always climbs
  // visibly from bottom-left to top-right on a smooth arc. Full arc at ~18s.
  const flightTime = isCrashed ? 20 : (isFlying ? localElapsed : 0);
  const rawProgress = Math.min(1, flightTime / 18);
  // Ease-out for a natural climbing feel (plane accelerates then coasts)
  const pathProgress = 1 - Math.pow(1 - rawProgress, 2.2);
  // Bezier-ish curve: plane starts low-left, arcs toward top-right
  const planeX = 8 + pathProgress * 80;                                // 8% → 88%
  const planeY = 82 - Math.sin(pathProgress * Math.PI * 0.5) * 62;     // 82% → 20% via sine arc
  const planeRotation = -30 - pathProgress * 15;                       // tilts up as it climbs

  return (
    <MobileLayout>
      <Toaster richColors position="top-center" />
      <audio ref={audioRef} src="/games/card-flip.mp3" preload="auto" />

      {/* DARK RADIAL SUNBURST BACKGROUND */}
      <div
        className="absolute left-0 right-0 top-16 bottom-0 -z-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 60%, #1a1a1a 0%, #050505 60%, #000 100%)" }}
      />
      {/* Radiating sunburst rays from center of arena */}
      <div
        className="absolute left-0 right-0 top-16 bottom-0 -z-0 pointer-events-none opacity-25"
        style={{
          background:
            "repeating-conic-gradient(from 0deg at 50% 40%, rgba(255,255,255,0.08) 0deg 3deg, transparent 3deg 12deg)",
        }}
      />

      <div className="relative z-10">
        {/* HEADER */}
        <div className="sticky top-0 z-30 -mx-3 px-3 py-2 mb-3 backdrop-blur-2xl bg-black/50 border-b border-amber-300/25 flex items-center justify-between">
          <Link to="/" data-testid="back-btn" className="w-8 h-8 rounded-full bg-white/5 border border-amber-300/30 flex items-center justify-center text-amber-200 active:scale-95 transition-transform">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex flex-col items-center leading-none">
            <div className="flex items-center gap-1.5">
              <Plane className="w-3.5 h-3.5 text-amber-300" />
              <span className="font-display font-black tracking-[0.35em] text-[12px]" style={{ color: "#f4d68a", textShadow: "0 0 14px rgba(244,214,138,0.5)" }}>AVIATOR</span>
              <Plane className="w-3.5 h-3.5 text-amber-300" style={{ transform: "scaleX(-1)" }} />
            </div>
            <span className="text-[8px] tracking-[0.4em] text-amber-200/40 mt-0.5">FLIGHT #{round.round_number}</span>
          </div>
          <div
            data-testid="wallet-chip"
            className="flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-amber-300/30 px-2.5 py-1 backdrop-blur-md"
          >
            <Wallet className="w-3 h-3 text-amber-200" />
            <span className="tabular-nums text-[11px] font-black" style={{ color: "#f4d68a" }}>₹{user?.wallet_balance ?? 0}</span>
          </div>
        </div>

        {/* FLIGHT ARENA */}
        <div
          className="relative mb-3 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.7)]"
          style={{
            height: 300,
            background: "radial-gradient(circle at 50% 55%, #1a1a1a 0%, #0a0a0a 60%, #000 100%)",
            border: "1.5px solid rgba(244,214,138,0.35)",
          }}
        >
          {/* Sunburst rays inside arena */}
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              background:
                "repeating-conic-gradient(from 0deg at 50% 55%, rgba(255,255,255,0.12) 0deg 2.5deg, transparent 2.5deg 14deg)",
            }}
          />
          {/* Subtle dotted grid */}
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
          {/* X-axis line at bottom */}
          <div className="absolute left-0 right-0 bottom-6 h-px bg-white/15 pointer-events-none" />
          {/* Y-axis line on left */}
          <div className="absolute top-4 bottom-6 left-6 w-px bg-white/15 pointer-events-none" />
          {/* Y-axis multiplier ticks (light blue dots) */}
          {[1, 2, 3, 5, 10].map((m, i) => (
            <div
              key={m}
              className="absolute w-1.5 h-1.5 rounded-full pointer-events-none"
              style={{
                left: "20px",
                bottom: `${28 + i * 42}px`,
                background: "#7dd3fc",
                boxShadow: "0 0 6px rgba(125,211,252,0.9)",
              }}
            />
          ))}

          {/* Red flight trail SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="trailFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"   stopColor="#dc2626" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#7f1d1d" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            {isFlying || isCrashed ? (
              <>
                {/* Filled area under arc */}
                <path
                  d={`M 8 82 Q ${8 + planeX * 0.25} 82 ${(8 + planeX) / 2} ${(82 + planeY) / 2 - 2} T ${planeX} ${planeY} L ${planeX} 82 Z`}
                  fill="url(#trailFill)"
                />
                {/* Dashed red trail line */}
                <path
                  d={`M 8 82 Q ${8 + planeX * 0.25} 82 ${(8 + planeX) / 2} ${(82 + planeY) / 2 - 2} T ${planeX} ${planeY}`}
                  stroke="#ef4444"
                  strokeWidth="1.5"
                  strokeDasharray="2 2"
                  fill="none"
                  strokeLinecap="round"
                  style={{ filter: "drop-shadow(0 0 4px rgba(239,68,68,0.95))" }}
                />
              </>
            ) : null}
          </svg>

          {/* Plane — original red propeller aircraft SVG */}
          {(isFlying || isCrashed) && (
            <div
              className="absolute"
              style={{
                left: `${planeX}%`,
                top: `${planeY}%`,
                transform: `translate(-50%, -50%) rotate(${isCrashed ? 75 : planeRotation}deg)`,
                transition: isCrashed ? "none" : "top 120ms linear, left 120ms linear, transform 400ms ease",
                filter: `drop-shadow(0 0 12px ${isCrashed ? "rgba(220,38,38,1)" : "rgba(239,68,68,0.95)"})`,
                animation: isCrashed ? "planeCrash 900ms ease-in forwards" : "none",
                zIndex: 5,
                width: 56, height: 56,
              }}
            >
              {isCrashed ? (
                <div style={{ fontSize: "48px", lineHeight: 1 }}>💥</div>
              ) : (
                <RedAircraft />
              )}
            </div>
          )}

          {/* WIN celebration overlay — pops up on successful cashout */}
          {winPopup && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 10 }}>
              <div
                className="rounded-2xl px-6 py-4 flex flex-col items-center gap-1"
                style={{
                  background: "linear-gradient(160deg, #10b981 0%, #064e3b 100%)",
                  border: "2px solid #a7f3d0",
                  boxShadow: "0 0 40px rgba(16,185,129,0.85), inset 0 1px 0 rgba(255,255,255,0.4)",
                  animation: "winPop 2800ms ease-out forwards",
                }}
              >
                <div className="text-[10px] font-black tracking-[0.4em] text-emerald-100">YOU WON</div>
                <div className="font-display font-black tabular-nums text-4xl text-white" style={{ textShadow: "0 0 20px rgba(255,255,255,0.6)" }}>
                  ₹{winPopup.amount}
                </div>
                <div className="text-[11px] font-black tracking-[0.3em] text-emerald-100">@ {winPopup.mult}x</div>
              </div>
              {/* Gold particle burst */}
              {Array.from({ length: 16 }).map((_, i) => {
                const angle = (i / 16) * 360;
                const rad = (angle * Math.PI) / 180;
                return (
                  <span
                    key={i}
                    className="absolute w-2 h-2 rounded-full"
                    style={{
                      background: "linear-gradient(135deg, #fde68a, #f59e0b)",
                      boxShadow: "0 0 8px #fbbf24",
                      animation: `winParticle 1200ms cubic-bezier(0.15,0.85,0.35,1) forwards`,
                      ["--dx"]: `${Math.cos(rad) * 110}px`,
                      ["--dy"]: `${Math.sin(rad) * 110}px`,
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* Central multiplier text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">            {isBetting ? (
              <>
                <div className="text-[10px] tracking-[0.4em] font-black text-amber-200/70">NEXT FLIGHT IN</div>
                <div
                  data-testid="countdown"
                  className={`font-display font-black tabular-nums leading-none mt-1 ${round.betting_time_left <= 3 ? "animate-pulse" : ""}`}
                  style={{
                    fontSize: "56px",
                    color: round.betting_time_left <= 3 ? "#fda4af" : "#f4d68a",
                    textShadow: `0 0 30px ${round.betting_time_left <= 3 ? "rgba(251,113,133,0.7)" : "rgba(244,214,138,0.6)"}`,
                  }}
                >
                  {Math.ceil(round.betting_time_left)}<span className="text-2xl opacity-70">s</span>
                </div>
                <div className="text-[9px] tracking-[0.35em] font-black text-amber-200/50 mt-1">PLACE YOUR BET</div>
              </>
            ) : (
              <>
                <div
                  data-testid="multiplier"
                  className="font-display font-black tabular-nums leading-none"
                  style={{
                    fontSize: "72px",
                    color: isCrashed ? "#ef4444" : "#ffffff",
                    textShadow: isCrashed
                      ? "0 0 40px rgba(239,68,68,0.95), 0 0 80px rgba(239,68,68,0.5)"
                      : "0 0 30px rgba(255,255,255,0.55)",
                    transform: isCrashed ? "scale(1.05)" : "scale(1)",
                    transition: "color 400ms ease, text-shadow 400ms ease",
                    animation: isCrashed ? "multCrashShake 500ms ease-in-out" : "none",
                  }}
                >
                  {displayMult.toFixed(2)}<span className="text-4xl opacity-90">x</span>
                </div>
                {isCrashed && (
                  <div
                    className="mt-3 font-display font-black tracking-[0.3em] text-2xl"
                    style={{
                      color: "#ef4444",
                      textShadow: "0 0 24px rgba(239,68,68,0.9)",
                      animation: "flewAwayFlash 700ms ease-out",
                    }}
                  >
                    FLEW AWAY!
                  </div>
                )}
              </>
            )}
          </div>

          {/* History strip inside arena bottom */}
          <div className="absolute top-2 left-2 right-2 flex gap-1 overflow-x-auto pb-0.5" data-testid="history-strip">
            {round.history?.slice(0, 12).map((h, i) => (
              <div
                key={i}
                data-testid={`history-${i}`}
                className="shrink-0 text-[10px] font-black tracking-wider px-1.5 py-0.5 rounded-md leading-none"
                style={{
                  background: "rgba(0,0,0,0.5)",
                  border: `1px solid ${multColor(h.crash_multiplier)}80`,
                  color: multColor(h.crash_multiplier),
                }}
              >
                {h.crash_multiplier.toFixed(2)}x
              </div>
            ))}
          </div>
        </div>

        {/* MY BET / CASH-OUT STATUS */}
        {myBet && (
          <div
            className="mb-3 rounded-xl p-2.5 flex items-center gap-3"
            style={{
              background: myCashed
                ? "linear-gradient(90deg, rgba(16,185,129,0.25), rgba(16,185,129,0.05))"
                : (isFlying ? "linear-gradient(90deg, rgba(251,146,60,0.25), rgba(251,146,60,0.05))" : "rgba(255,255,255,0.04)"),
              border: `1px solid ${myCashed ? "#10b981" : (isFlying ? "#fb923c" : "rgba(244,214,138,0.25)")}`,
            }}
          >
            <div className="flex-1">
              <div className="text-[9px] tracking-[0.3em] font-black text-amber-200/70">MY BET</div>
              <div className="text-xl font-black tabular-nums text-white">₹{myBet.amount}</div>
            </div>
            {myCashed ? (
              <div className="text-right">
                <div className="text-[9px] tracking-[0.3em] font-black text-emerald-300">CASHED OUT</div>
                <div className="text-xl font-black tabular-nums text-emerald-200">₹{(myBet.amount * myBet.cashout_multiplier).toFixed(2)}</div>
                <div className="text-[10px] font-black text-emerald-200/80">@ {myBet.cashout_multiplier}x</div>
              </div>
            ) : isBetting ? (
              <div className="text-right">
                <div className="text-[9px] tracking-[0.3em] font-black text-amber-300">LOCKED</div>
                {myBet.auto_cashout && <div className="text-[10px] font-black text-amber-200">auto @{myBet.auto_cashout}x</div>}
              </div>
            ) : null}
          </div>
        )}

        {/* CASH OUT / BET BUTTONS */}
        {canCashOut ? (
          <button
            data-testid="cashout-btn"
            onClick={cashOut}
            disabled={cashingOut}
            className="w-full rounded-2xl h-[92px] flex flex-col items-center justify-center relative overflow-hidden mb-3 active:scale-95 transition-transform"
            style={{
              background: "linear-gradient(180deg, #f59e0b 0%, #b45309 100%)",
              border: "2px solid #fef3c7",
              boxShadow: "0 0 30px rgba(251,146,60,0.65), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -4px 8px rgba(0,0,0,0.35)",
              animation: "cashoutPulse 800ms ease-in-out infinite",
            }}
          >
            <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
            <div className="relative flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-950" fill="currentColor" />
              <span className="font-display font-black tracking-[0.25em] text-[18px] text-amber-950">CASH OUT</span>
            </div>
            <div className="relative text-amber-900 font-black text-[13px] tabular-nums mt-0.5">
              ₹{(myBet.amount * displayMult).toFixed(2)} @ {displayMult.toFixed(2)}x
            </div>
          </button>
        ) : isBetting && !myBet ? (
          <button
            data-testid="place-bet-btn"
            onClick={placeBet}
            disabled={placing}
            className="w-full rounded-2xl h-[64px] flex items-center justify-center gap-2 relative overflow-hidden mb-3 active:scale-95 transition-transform disabled:opacity-50"
            style={{
              background: "linear-gradient(180deg, #10b981 0%, #065f46 100%)",
              border: "2px solid #a7f3d0",
              boxShadow: "0 0 22px rgba(16,185,129,0.55), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -3px 8px rgba(0,0,0,0.35)",
            }}
          >
            <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
            <span className="relative font-display font-black tracking-[0.25em] text-[16px] text-emerald-950">PLACE BET · ₹{amount}</span>
          </button>
        ) : (
          <button
            disabled
            data-testid="cashout-btn"
            className="w-full rounded-2xl h-[64px] flex items-center justify-center gap-2 mb-3 opacity-50"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(244,214,138,0.2)",
            }}
          >
            <span className="font-display font-black tracking-[0.3em] text-[13px] text-amber-200/60">
              {isFlying ? "IN FLIGHT — PLACE BET NEXT ROUND" : (isCrashed ? "WAITING FOR NEXT FLIGHT" : "WAITING…")}
            </span>
          </button>
        )}

        {/* BET AMOUNT + AUTO CASHOUT */}
        <div
          data-testid="amount-selector"
          className="rounded-xl p-2.5 mb-3"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))",
            border: "1px solid rgba(244,214,138,0.25)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-black text-[13px]"
              style={{
                background: "radial-gradient(circle at 30% 30%, #f4d68a, #8a5a12)",
                color: "#3d2005",
                boxShadow: "inset 0 -1px 3px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.4)",
              }}
            >
              ₹
            </div>
            <input
              type="number"
              min={round.min_bet}
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
              data-testid="amount-input"
              disabled={!!myBet}
              className="flex-1 min-w-0 text-lg font-black text-amber-100 tabular-nums bg-transparent outline-none placeholder:text-white/30 disabled:opacity-50"
            />
            <div className="text-[9px] text-amber-200/40 tracking-[0.2em] shrink-0">MIN ₹{round.min_bet}</div>
          </div>
          <div className="grid grid-cols-5 gap-1 mb-2">
            {PRESETS.map(v => {
              const active = amount === v;
              return (
                <button
                  key={v}
                  onClick={() => setAmount(v)}
                  disabled={!!myBet}
                  data-testid={`preset-${v}`}
                  className="h-8 rounded-lg font-black text-[11px] tabular-nums transition-transform active:scale-90 disabled:opacity-50"
                  style={{
                    background: active ? "linear-gradient(135deg, #f4d68a, #b57d1e)" : "rgba(255,255,255,0.03)",
                    border: active ? "1px solid #f4d68a" : "1px solid rgba(212,169,74,0.2)",
                    color: active ? "#3d2005" : "#f4d68a",
                    boxShadow: active ? "0 0 10px rgba(244,214,138,0.5)" : "none",
                  }}
                >
                  {v >= 1000 ? `${v/1000}K` : v}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[9px] tracking-[0.2em] font-black text-amber-200/70">AUTO @</div>
            <input
              type="number"
              min="1.1"
              step="0.1"
              placeholder="off"
              value={autoCashout}
              disabled={!!myBet}
              onChange={(e) => setAutoCashout(e.target.value)}
              data-testid="auto-cashout-input"
              className="flex-1 text-sm font-black text-amber-100 tabular-nums bg-white/5 border border-amber-300/25 rounded-lg px-2 py-1 outline-none placeholder:text-white/30 disabled:opacity-50"
            />
            <div className="text-[9px] text-amber-200/40 tracking-[0.2em]">optional</div>
          </div>
        </div>

        {/* LIVE PLAYERS FEED */}
        <div
          className="rounded-xl p-2.5 mb-3"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))",
            border: "1px solid rgba(244,214,138,0.25)",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-amber-200/70 font-black">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE BETS
            </div>
            <div className="text-[9px] font-black text-amber-200/40 tabular-nums">{livePlayers.length} playing</div>
          </div>
          <div className="max-h-[140px] overflow-y-auto space-y-1">
            {livePlayers.map((p, i) => {
              const cashedNow = p.target && isFlying && liveMult >= p.target;
              const cashedCrashed = p.target && isCrashed && p.target <= (round.crash_multiplier || 0);
              const cashed = cashedNow || cashedCrashed;
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg px-2 py-1"
                  style={{
                    background: cashed ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${cashed ? "rgba(16,185,129,0.4)" : "rgba(244,214,138,0.12)"}`,
                  }}
                >
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white shrink-0"
                    style={{ background: `hsl(${(i * 47) % 360} 60% 45%)` }}
                  >
                    {p.name[0]}
                  </div>
                  <div className="flex-1 text-[11px] font-black text-amber-100/90 tabular-nums truncate">{p.name}</div>
                  <div className="text-[10px] font-black text-white tabular-nums">₹{p.amount}</div>
                  {cashed ? (
                    <div className="text-[10px] font-black tracking-wider tabular-nums text-emerald-300 min-w-[52px] text-right">
                      {p.target.toFixed(2)}x ₹{(p.amount * p.target).toFixed(0)}
                    </div>
                  ) : (isBetting || (!cashed && !isCrashed)) ? (
                    <div className="text-[10px] font-black tracking-wider text-amber-200/60 min-w-[52px] text-right">
                      {isBetting ? "…" : "flying"}
                    </div>
                  ) : (
                    <div className="text-[10px] font-black tracking-wider text-rose-400/70 min-w-[52px] text-right">lost</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* FULL HISTORY BUTTON */}
        <div
          className="rounded-xl p-2.5 mb-3 flex items-center justify-between"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))",
            border: "1px solid rgba(244,214,138,0.25)",
          }}
        >
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-amber-200/70 font-black">
            <History className="w-3 h-3" /> LAST {round.history?.length || 0} FLIGHTS
          </div>
          <button
            onClick={() => setShowHistoryModal(true)}
            data-testid="game-history-btn"
            className="flex items-center gap-0.5 text-[10px] font-black tracking-wider text-amber-200/80"
          >
            OPEN <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {showHistoryModal && (
          <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-end animate-fade-in" onClick={() => setShowHistoryModal(false)}>
            <div className="w-full max-w-md mx-auto bg-gradient-to-b from-[#1a0a06] to-black border-t-2 border-amber-300/60 rounded-t-3xl p-4 max-h-[75vh] overflow-y-auto pb-24" onClick={e => e.stopPropagation()}>
              <div className="text-center mb-4">
                <div className="font-display font-black text-xl tracking-[0.2em]" style={{ color: "#f4d68a" }}>CRASH HISTORY</div>
                <div className="text-[10px] tracking-widest text-amber-200/50 mt-1">Latest first</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {round.history?.map((h, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 border border-amber-300/15">
                    <div
                      className="w-full py-1.5 rounded-lg text-center font-black text-sm tabular-nums"
                      style={{
                        background: `${multColor(h.crash_multiplier)}18`,
                        border: `1px solid ${multColor(h.crash_multiplier)}`,
                        color: multColor(h.crash_multiplier),
                      }}
                    >
                      {h.crash_multiplier.toFixed(2)}x
                    </div>
                    <div className="text-[9px] font-black text-amber-200/60 tracking-wider">#{h.round_number}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 0.9; }
        }
        @keyframes sunPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          50%      { transform: translate(-50%, -50%) scale(1.08); opacity: 0.9; }
        }
        @keyframes planeShake {
          0%, 100% { transform: translate(-50%, -50%) rotate(-25deg) translateY(0); }
          50%      { transform: translate(-50%, -50%) rotate(-24deg) translateY(-2px); }
        }
        @keyframes planeCrash {
          0%   { transform: translate(-50%, -50%) rotate(45deg); opacity: 1; }
          70%  { transform: translate(-30%, 100%) rotate(120deg); opacity: 1; }
          100% { transform: translate(-30%, 200%) rotate(200deg); opacity: 0; }
        }
        @keyframes multCrashShake {
          0%, 100% { transform: scale(1.05); }
          25%      { transform: translate(-4px, 0) scale(1.05); }
          50%      { transform: translate(4px, 0) scale(1.05); }
          75%      { transform: translate(-2px, 0) scale(1.05); }
        }
        @keyframes cashoutPulse {
          0%, 100% { box-shadow: 0 0 30px rgba(251,146,60,0.65), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -4px 8px rgba(0,0,0,0.35); }
          50%      { box-shadow: 0 0 50px rgba(251,146,60,0.95), inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -4px 8px rgba(0,0,0,0.35); }
        }
        @keyframes flewAwayFlash {
          0%   { transform: scale(0.6); opacity: 0; letter-spacing: 0.1em; }
          40%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; letter-spacing: 0.3em; }
        }
        @keyframes propSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes winPop {
          0%   { transform: scale(0.4); opacity: 0; }
          15%  { transform: scale(1.15); opacity: 1; }
          25%  { transform: scale(1); opacity: 1; }
          85%  { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0; }
        }
        @keyframes winParticle {
          0%   { transform: translate(0,0) scale(0.4); opacity: 0; }
          20%  { transform: translate(calc(var(--dx)*0.3), calc(var(--dy)*0.3)) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0; }
        }
      `}</style>
    </MobileLayout>
  );
}

/* Original stylized red propeller aircraft (custom SVG) */
function RedAircraft() {
  return (
    <svg viewBox="0 0 64 64" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="fuse" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#991b1b" />
        </linearGradient>
        <linearGradient id="wing" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#dc2626" />
          <stop offset="100%" stopColor="#7f1d1d" />
        </linearGradient>
      </defs>
      {/* Bottom wing */}
      <ellipse cx="26" cy="42" rx="20" ry="4" fill="url(#wing)" stroke="#450a0a" strokeWidth="0.8"/>
      {/* Fuselage (body) */}
      <path d="M 6 30 Q 6 26 12 26 L 46 24 Q 54 25 55 30 Q 54 35 46 36 L 12 34 Q 6 34 6 30 Z" fill="url(#fuse)" stroke="#450a0a" strokeWidth="0.8"/>
      {/* Cockpit window */}
      <path d="M 36 27 Q 40 26 43 28 Q 44 30 43 32 Q 40 34 36 33 Z" fill="#fef3c7" opacity="0.85"/>
      {/* Top wing */}
      <ellipse cx="26" cy="20" rx="18" ry="3" fill="url(#wing)" stroke="#450a0a" strokeWidth="0.8"/>
      {/* Wing struts */}
      <line x1="18" y1="23" x2="18" y2="28" stroke="#450a0a" strokeWidth="0.8"/>
      <line x1="34" y1="22" x2="34" y2="27" stroke="#450a0a" strokeWidth="0.8"/>
      {/* Tail vertical */}
      <path d="M 8 22 L 12 26 L 12 30 L 6 30 Z" fill="url(#fuse)" stroke="#450a0a" strokeWidth="0.8"/>
      {/* Tail horizontal */}
      <ellipse cx="8" cy="32" rx="7" ry="2" fill="url(#wing)" stroke="#450a0a" strokeWidth="0.8"/>
      {/* Propeller (spinning) */}
      <g style={{ transformOrigin: "55px 30px", animation: "propSpin 100ms linear infinite" }}>
        <ellipse cx="55" cy="30" rx="1.2" ry="10" fill="#fbbf24" opacity="0.85"/>
        <ellipse cx="55" cy="30" rx="10" ry="1.2" fill="#fbbf24" opacity="0.85"/>
      </g>
      <circle cx="55" cy="30" r="2" fill="#7f1d1d" stroke="#450a0a" strokeWidth="0.5"/>
    </svg>
  );
}
